import React, { useState } from "react";

// --- Datos del juego ---
const teams = ["River Plate", "Boca Juniors", "Racing Club", "Independiente", "San Lorenzo", "Velez Sarsfield", "Estudiantes", "Rosario Central", "Real Madrid", "PSG"];
const animals = ["León", "Tigre", "Elefante", "Jirafa", "Delfín", "Cebra", "Pingüino", "Ballena", "Oso Panda", "Canguro"];
const countries = ["Chile", "Brasil", "Japón", "Francia", "Italia", "Australia", "Egipto", "Canadá", "Corea del Sur", "España"];
const movies = ["Titanic", "Spider Man", "Avatar", "Iron Man", "El Rey León", "Matrix", "Rápidos y furiosos", "Star Wars", "Jurassic Park", "Rocky", "Deadpool", "X-MEN", "Mi pobre angelito", "¿Donde están las rubias?"];
const pcGames = ["Minecraft", "Counter-Strike", "The Sims", "League of Legends", "Among us", "Roblox", "Free Fire", "Fortnite", "Age of Empires", "GTA 5"];
const foods = ["Pizza", "Hamburguesa", "Sushi", "Tacos", "Asado", "Pasta", "Paella", "Empanadas", "Panchos", "Ramen"];

const categories = [
  { name: "Club de Fútbol", pool: teams },
  { name: "Animal", pool: animals },
  { name: "País", pool: countries },
  { name: "Película", pool: movies },
  { name: "Juego de PC", pool: pcGames },
  { name: "Comida", pool: foods }
];

// --- Estilos para un diseño "misterioso" y "atrapante" ---
const styles = {
  container: {
    textAlign: "center",
    padding: "20px",
    minHeight: "100vh",
    background: "#121212", // Fondo oscuro
    color: "#e0e0e0", // Texto claro
    fontFamily: "'Courier New', Courier, monospace", // Fuente que evoca misterio/código
  },
  title: {
    fontSize: "4em",
    color: "#ff0066", // Color llamativo (rojo/fucsia neón)
    textShadow: "0 0 10px #ff0066, 0 0 20px #ff0066", // Efecto neón
    marginBottom: "30px",
    letterSpacing: "5px",
  },
  button: {
    padding: "12px 25px",
    fontSize: "20px",
    background: "#00cc66", // Botón verde neón
    color: "#121212",
    border: "2px solid #00cc66",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background 0.3s, color 0.3s, border-color 0.3s",
    fontWeight: "bold",
  },
  input: {
    marginLeft: "10px",
    padding: "8px",
    border: "1px solid #ff0066",
    background: "#222",
    color: "#e0e0e0",
    borderRadius: "4px",
    width: "150px",
  },
  error: {
    color: "#ff0066",
    marginTop: "15px",
    fontWeight: "bold",
  },
  card: {
    background: "#222",
    padding: "30px",
    borderRadius: "10px",
    boxShadow: "0 0 15px rgba(255, 0, 102, 0.5)", // Sombra neón
    maxWidth: "400px",
    margin: "30px auto",
  }
};

function shuffle(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

export default function App() {
  // Nuevo estado para la navegación entre menús
  const [gameState, setGameState] = useState('menu'); // 'menu', 'config', 'nameEntry', 'game'

  // Estados de configuración
  const [numPlayers, setNumPlayers] = useState(4); // Valor inicial más razonable
  const [numImpostors, setNumImpostors] = useState(1);
  const [playerNames, setPlayerNames] = useState([]); // Nombres dinámicos
  const [nameInputs, setNameInputs] = useState(Array(4).fill("")); // Para capturar los nombres

  // Estados del juego
  const [roles, setRoles] = useState([]);
  const [category, setCategory] = useState(null);
  const [item, setItem] = useState(null);
  const [roundStarted, setRoundStarted] = useState(false);
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const [currentRoleVisible, setCurrentRoleVisible] = useState(false);
  const [impostorIndices, setImpostorIndices] = useState([]);
  const [error, setError] = useState("");

  const numRegularPlayers = numPlayers - numImpostors;

  // --- Funciones de navegación y manejo de estado ---

  // Navegar de "Menú" a "Configuración"
  const goToConfig = () => {
    setGameState('config');
  };

  // Manejar el cambio de nombres en la pantalla de entrada de nombres
  const handleNameChange = (index, name) => {
    const newNameInputs = [...nameInputs];
    newNameInputs[index] = name;
    setNameInputs(newNameInputs);
  };

  // Ir de "Configuración de Jugadores/Impostores" a "Entrada de Nombres"
  const goToNameEntry = () => {
    if (numPlayers <= 0 || numImpostors < 0) {
      setError("Debes ingresar números válidos.");
      return;
    }
    if (numImpostors >= numPlayers) {
      setError("Los impostores no pueden ser iguales o más que los jugadores.");
      return;
    }

    // Inicializa la matriz de entrada de nombres con el nuevo número de jugadores
    setNameInputs(Array(numPlayers).fill(""));
    setGameState('nameEntry');
    setError("");
  };

  // Iniciar la ronda después de ingresar los nombres
  const confirmNamesAndStartRound = () => {
    const validNames = nameInputs.filter(name => name.trim() !== "");
    if (validNames.length !== numPlayers) {
      setError(`Debes ingresar ${numPlayers} nombres.`);
      return;
    }

    setPlayerNames(nameInputs); // Guarda los nombres definitivos
    startRoundLogic(nameInputs); // Llama a la lógica del juego
  };

  // Lógica principal para iniciar la ronda
  const startRoundLogic = (currentNames) => {
    // La validación ya se hizo en goToNameEntry
    setError("");

    const chosenCategory = categories[Math.floor(Math.random() * categories.length)];
    const chosenItem = chosenCategory.pool[Math.floor(Math.random() * chosenCategory.pool.length)];
    setCategory(chosenCategory.name);
    setItem(chosenItem);

    const rolePool = Array(numRegularPlayers).fill(chosenItem).concat(Array(numImpostors).fill("Impostor"));
    const shuffledRoles = shuffle(rolePool);
    setRoles(shuffledRoles);

    // Mapea los índices de los impostores
    setImpostorIndices(
      shuffledRoles.map((role, idx) => (role === "Impostor" ? idx : null)).filter((idx) => idx !== null)
    );

    setCurrentPlayerIndex(0);
    setCurrentRoleVisible(false);
    setRoundStarted(true);
    setGameState('game'); // Pasa al estado de juego
  };

  const showRole = () => setCurrentRoleVisible(true);
  
  const nextPlayer = () => {
      // Oculta el rol antes de pasar al siguiente jugador
      setCurrentRoleVisible(false); 
      setCurrentPlayerIndex((i) => i + 1);
  }

  const resetGame = () => {
    setRoles([]);
    setCategory(null);
    setItem(null);
    setRoundStarted(false);
    setCurrentPlayerIndex(0);
    setCurrentRoleVisible(false);
    setImpostorIndices([]);
  };

  const nextRoundSameConfig = () => {
    resetGame();
    // Usa los nombres ya guardados en playerNames
    startRoundLogic(playerNames);
  };
  
  const goToNewConfig = () => {
      resetGame();
      setGameState('config'); // Vuelve a la configuración de números
      setPlayerNames([]); // Limpia los nombres
  };

  // --- Renderizado de Vistas ---

  // 1. Pantalla de Menú Principal
  if (gameState === 'menu') {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>IMPOSTHOR 🔪</h1>
        <div style={styles.card}>
          <p style={{ marginBottom: 30, fontSize: 18 }}>¿Estás listo para el engaño?</p>
          <button
            onClick={goToConfig}
            style={{ ...styles.button, background: "#ff0066", border: "2px solid #ff0066", color: "#e0e0e0" }}
            onMouseOver={(e) => e.currentTarget.style.boxShadow = "0 0 20px #ff0066"}
            onMouseOut={(e) => e.currentTarget.style.boxShadow = "none"}
          >
            JUGAR
          </button>
        </div>
      </div>
    );
  }

  // 2. Pantalla de Configuración de Jugadores/Impostores
  if (gameState === 'config') {
    return (
      <div style={styles.container}>
        <h1 style={{ ...styles.title, fontSize: "2.5em" }}>⚙️ Configuración</h1>
        <div style={styles.card}>
          <div style={{ marginTop: 20 }}>
            <label style={{ fontSize: 18 }}>
              Jugadores:
              <input
                type="number"
                min="1"
                max="10"
                value={numPlayers}
                onChange={(e) => setNumPlayers(parseInt(e.target.value) || 0)}
                style={{ ...styles.input, width: 80 }}
              />
            </label>
          </div>
          <div style={{ marginTop: 20 }}>
            <label style={{ fontSize: 18 }}>
              Impostores:
              <input
                type="number"
                min="0"
                max={numPlayers - 1}
                value={numImpostors}
                onChange={(e) => setNumImpostors(parseInt(e.target.value) || 0)}
                style={{ ...styles.input, width: 80 }}
              />
            </label>
          </div>
          {error && <p style={styles.error}>{error}</p>}
          <button
            onClick={goToNameEntry}
            style={{ ...styles.button, marginTop: 30, background: "#00cc66" }}
          >
            Siguiente: Nombres
          </button>
        </div>
      </div>
    );
  }

  // 3. Pantalla de Entrada de Nombres
  if (gameState === 'nameEntry') {
    return (
      <div style={styles.container}>
        <h1 style={{ ...styles.title, fontSize: "2.5em" }}>✏️ Nombres de Jugadores</h1>
        <div style={styles.card}>
          <p>Introduce los nombres de los **{numPlayers}** participantes:</p>
          <div style={{ maxHeight: 250, overflowY: 'auto', paddingRight: 15, marginBottom: 20 }}>
            {Array.from({ length: numPlayers }).map((_, index) => (
              <div key={index} style={{ margin: "10px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <label>Jugador {index + 1}:</label>
                <input
                  type="text"
                  placeholder={`Nombre ${index + 1}`}
                  value={nameInputs[index]}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  style={{ ...styles.input, width: 150 }}
                />
              </div>
            ))}
          </div>
          {error && <p style={styles.error}>{error}</p>}
          <button
            onClick={confirmNamesAndStartRound}
            style={{ ...styles.button, marginTop: 15, background: "#00cc66" }}
          >
            Comenzar Ronda
          </button>
        </div>
      </div>
    );
  }

  // 4. Pantalla de Juego (Lógica Original)
  if (gameState === 'game') {
    return (
      <div style={styles.container}>
        <h1 style={{ ...styles.title, fontSize: "2.5em" }}>🎮 IMPOSTHOR</h1>

        {currentPlayerIndex < numPlayers ? (
          <div style={styles.card}>
            <h2>Turno de: **{playerNames[currentPlayerIndex]}**</h2>
            {!currentRoleVisible ? (
              <button onClick={showRole} style={{ ...styles.button, background: "#008CBA", color: "white" }}>
                Revelar Mi Rol
              </button>
            ) : (
              <div>
                <h3>Categoría: **{category}**</h3>
                <p style={{
                  fontSize: 36,
                  fontWeight: "bold",
                  color: roles[currentPlayerIndex] === "Impostor" ? "#ff0066" : "#00cc66"
                }}>
                  {roles[currentPlayerIndex]}
                </p>
                {roles[currentPlayerIndex] === "Impostor" && impostorIndices.length > 1 && (
                  <>
                    <p style={{ marginTop: 15 }}>Tus compañeros impostores son:</p>
                    <ul style={{ listStyle: "none", padding: 0, color: "#e0e0e0" }}>
                      {impostorIndices.filter((i) => i !== currentPlayerIndex).map((i) => (
                        <li key={i}>**{playerNames[i]}**</li>
                      ))}
                    </ul>
                  </>
                )}
                <button
                  onClick={nextPlayer}
                  style={{ ...styles.button, marginTop: 20, background: "#f44336", color: "white" }}
                >
                  He visto mi rol / Siguiente
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={styles.card}>
            <h2>¡Todos vieron su rol!</h2>
            <p style={{ fontSize: 18, marginBottom: 20 }}>Es hora de la discusión. ¿Quién será el impostor?</p> 
            {/* Se elimina: <p>La palabra fue: <strong>{item}</strong> ({category})</p> */}
            <button
              onClick={nextRoundSameConfig}
              style={{ ...styles.button, marginTop: 20, background: "#00cc66", marginRight: 15 }}
            >
              🔁 Otra Ronda
            </button>
            <button
              onClick={goToNewConfig}
              style={{ ...styles.button, marginTop: 20, background: "#6c757d", color: "white" }}
            >
              ⚙️ Nueva Configuración
            </button>
          </div>
        )}
      </div>
    );
  }
}